import React from 'react';
import {
  X, MapPin, Clock, ThumbsUp, MessageCircle, Share2,
  BadgeCheck, TrendingUp, Car, Fuel, ShoppingBag, Calendar,
  Zap, Droplet, AlertTriangle, ChevronLeft, Edit2, Trash2
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { QuestionSection, Question } from './QuestionSection';

interface Report {
  id: string;
  type?: string;
  category?: 'traffic' | 'fuel' | 'market' | 'event' | 'power' | 'flood' | 'other';
  location: string;
  state?: string;
  region?: string;
  lga?: string;
  content?: string;
  description?: string;
  timestamp: string;
  userName?: string;
  reporter?: string;
  userTrustScore?: number;
  reporterScore?: number;
  reporterRole?: 'user' | 'agent' | 'admin';
  likes?: number;
  upvotes?: number;
  imageUrl?: string;
  isOfficial?: boolean;
  comments?: Question[];
}

interface ReportDetailModalProps {
  report: Report | null;
  isOpen: boolean;
  onClose: () => void;
  onUpvote?: (reportId: string) => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onShare?: () => void;
  currentUser?: any;
  onAddComment?: (postId: string, text: string) => void;
}

const categoryConfig = {
  traffic: { icon: Car, label: 'Traffic Update', color: 'bg-red-500', textColor: 'text-red-600' },
  fuel: { icon: Fuel, label: 'Fuel Status', color: 'bg-orange-500', textColor: 'text-orange-600' },
  market: { icon: ShoppingBag, label: 'Market Price', color: 'bg-purple-500', textColor: 'text-purple-600' },
  event: { icon: Calendar, label: 'Event Notice', color: 'bg-blue-500', textColor: 'text-blue-600' },
  power: { icon: Zap, label: 'Power Status', color: 'bg-yellow-500', textColor: 'text-yellow-600' },
  flood: { icon: Droplet, label: 'Flood Alert', color: 'bg-cyan-500', textColor: 'text-cyan-600' },
  other: { icon: AlertTriangle, label: 'Other Report', color: 'bg-zinc-500', textColor: 'text-zinc-600' },
};

export function ReportDetailModal({ report, isOpen, onClose, onUpvote, onEdit, onDelete, onShare, currentUser, onAddComment }: ReportDetailModalProps) {
  // Early return if no report - prevents rendering issues
  if (!report) return null;

  // Prevent body scroll when modal is open
  React.useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Handle both old and new report formats
  const reportCategory = (report.category || report.type || 'other') as keyof typeof categoryConfig;
  const reportDescription = report.description || report.content || 'No description';
  const reporterName = report.reporter || report.userName || 'Anonymous';
  const reporterScore = report.reporterScore || report.userTrustScore || 0;
  const votesCount = report.upvotes || report.likes || 0;
  const isOwnPost = currentUser && (report.userName === currentUser.name || report.reporter === currentUser.name);
  
  const config = categoryConfig[reportCategory] || categoryConfig.other;
  const CategoryIcon = config.icon;

  return (
    <AnimatePresence mode="wait">
      {isOpen && (
        <React.Fragment key={report.id}>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, y: '100%' }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed inset-x-0 bottom-0 z-50 bg-white rounded-t-3xl shadow-2xl max-h-[90vh] overflow-y-auto"
          >
            {/* Header */}
            <div className="sticky top-0 bg-white border-b border-zinc-200 px-4 py-4 z-10">
              <div className="flex items-center justify-between">
                <button
                  onClick={onClose}
                  className="flex items-center gap-2 text-zinc-600 hover:text-zinc-900 font-bold text-sm"
                >
                  <ChevronLeft className="w-5 h-5" />
                  Back to Feed
                </button>
                <button
                  onClick={onClose}
                  className="w-10 h-10 rounded-full bg-zinc-100 hover:bg-zinc-200 flex items-center justify-center transition-colors"
                >
                  <X className="w-5 h-5 text-zinc-600" />
                </button>
              </div>
            </div>

            {/* Image */}
            {report.imageUrl && (
              <div className="relative h-64 bg-zinc-100">
                <img
                  src={report.imageUrl}
                  alt={report.location}
                  className="w-full h-full object-cover"
                />
              </div>
            )}

            {/* Content */}
            <div className="p-6 space-y-6">
              {/* Category Badge */}
              <div className="flex items-center justify-between">
                <div className={`${config.color} text-white px-4 py-2.5 rounded-xl flex items-center gap-2.5 shadow-md`}>
                  <CategoryIcon className="w-5 h-5" />
                  <span className="text-sm font-bold uppercase tracking-wider">{config.label}</span>
                </div>
                {report.isOfficial && (
                  <div className="bg-green-50 text-green-700 px-3 py-1.5 rounded-full flex items-center gap-1.5">
                    <BadgeCheck className="w-4 h-4" />
                    <span className="text-xs font-bold">Official</span>
                  </div>
                )}
              </div>

              {/* Location */}
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <MapPin className="w-5 h-5 text-green-600" />
                  <h2 className="text-2xl font-black text-zinc-900">{report.location}</h2>
                </div>
                {report.lga && report.lga !== report.location && (
                  <p className="text-sm text-zinc-500 ml-7">{report.lga}</p>
                )}
                {report.state && (
                  <p className="text-sm text-zinc-500 ml-7">{report.state}</p>
                )}
              </div>

              {/* Description */}
              <div className="bg-zinc-50 rounded-xl p-4">
                <p className="text-base text-zinc-800 leading-relaxed">
                  {reportDescription}
                </p>
              </div>

              {/* Reporter Info */}
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-4">
                <p className="text-xs font-bold text-zinc-500 uppercase tracking-wider mb-3">
                  Reported By
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center text-white text-lg font-bold shadow-md">
                    {reporterName[0]?.toUpperCase()}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-base font-black text-zinc-900">{reporterName}</span>
                      {(report.reporterRole === 'agent' || report.reporterRole === 'admin' || report.isOfficial) && (
                        <BadgeCheck className="w-5 h-5 text-green-600" />
                      )}
                    </div>
                    {reporterScore && (
                      <div className="flex items-center gap-1.5 mt-1">
                        <TrendingUp className="w-4 h-4 text-green-600" />
                        <span className="text-sm font-bold text-green-600">{reporterScore} Trust Score</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Timestamp */}
              <div className="flex items-center gap-2 text-zinc-500">
                <Clock className="w-4 h-4" />
                <span className="text-sm font-medium">Reported {report.timestamp}</span>
              </div>

              {/* Actions */}
              <div className="grid grid-cols-2 gap-3 pt-2">
                <button
                  onClick={() => onUpvote?.(report.id)}
                  className="flex items-center justify-center gap-2 px-6 py-4 rounded-xl bg-green-500 hover:bg-green-600 active:scale-95 transition-all shadow-md text-white"
                >
                  <ThumbsUp className="w-5 h-5" />
                  <div className="text-left">
                    <div className="text-xs font-medium opacity-90">Verify Report</div>
                    <div className="text-base font-black">
                      {votesCount} {votesCount === 1 ? 'Vote' : 'Votes'}
                    </div>
                  </div>
                </button>

                <button
                  onClick={onShare}
                  className="flex items-center justify-center gap-2 px-6 py-4 rounded-xl bg-zinc-100 hover:bg-zinc-200 active:scale-95 transition-all border-2 border-zinc-200"
                >
                  <Share2 className="w-5 h-5 text-zinc-700" />
                  <div className="text-left">
                    <div className="text-xs font-medium text-zinc-500">Share</div>
                    <div className="text-base font-black text-zinc-900">Forward</div>
                  </div>
                </button>
              </div>

              {/* Community Note */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <MessageCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs font-bold text-blue-900 mb-1">Community Verified</p>
                    <p className="text-sm text-blue-700">
                      This report has been verified by {votesCount} community members in your area.
                    </p>
                  </div>
                </div>
              </div>

              {/* Comments Section */}
              {onAddComment && (
                <div className="border-t border-zinc-200 pt-4">
                  <QuestionSection 
                    postId={report.id} 
                    questions={report.comments || []}
                    onAddQuestion={onAddComment}
                  />
                </div>
              )}
            </div>
          </motion.div>
        </React.Fragment>
      )}
    </AnimatePresence>
  );
}